import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getOrder, getVendors, updateOrder, finalizeOrder, deleteOrder, getOrderVendorsStatus, api } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Loader2, 
  Download, 
  CheckCircle, 
  Clock, 
  Minus, 
  Plus, 
  Truck,
  RefreshCw,
  ChevronDown,
  FileText,
  Trash2,
  PackageCheck,
  ArrowRight,
  Share2,
  ExternalLink,
  PlusCircle
} from "lucide-react";

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  
  const [order, setOrder] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [orderVendors, setOrderVendors] = useState([]);
  const [editedItems, setEditedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Vendor status for receive tracking
  const [vendorStatus, setVendorStatus] = useState(null);

  useEffect(() => {
    loadData();
  }, [orderId]);

  const loadData = async () => {
    try {
      const [orderRes, vendorsRes, orderVendorsRes] = await Promise.all([
        getOrder(orderId),
        getVendors(),
        api.get(`/orders/${orderId}/vendors`),
      ]);
      setOrder(orderRes.data);
      setVendors(vendorsRes.data);
      setOrderVendors(orderVendorsRes.data.vendors || []);
      setEditedItems(orderRes.data.items.map(item => ({ ...item })));
      
      // Load vendor status for finalized/partial orders
      if (["finalized", "partially_received", "received"].includes(orderRes.data.status)) {
        try {
          const statusRes = await getOrderVendorsStatus(orderId);
          setVendorStatus(statusRes.data);
        } catch (e) {
          console.error("Error loading vendor status:", e);
        }
      }
    } catch (error) {
      toast.error("Failed to load order");
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  const updateItem = (index, field, value) => {
    setEditedItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        ...(field === "vendor_id" && {
          vendor_name: vendors.find((v) => v.id === value)?.name || "Unknown",
        }),
      };
      return updated;
    });
  };

  const adjustQuantity = (index, delta) => {
    const currentValue = editedItems[index]?.quantity_to_order || 0;
    const newValue = Math.max(0, currentValue + delta);
    updateItem(index, "quantity_to_order", newValue);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateOrder(orderId, { items: editedItems });
      toast.success("Order updated");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalize = async () => {
    setIsFinalizing(true);
    try {
      // First save any pending changes
      await updateOrder(orderId, { items: editedItems });
      // Then finalize
      await finalizeOrder(orderId);
      toast.success("Order finalized!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to finalize order");
    } finally {
      setIsFinalizing(false);
    }
  };

  // Open PDF in browser (inline)
  const handleOpenPdf = async (vendorName = null) => {
    setIsDownloading(true);
    try {
      const params = vendorName ? `?vendor_name=${encodeURIComponent(vendorName)}` : "";
      const response = await api.get(`/orders/${orderId}/pdf${params}`, { responseType: "blob" });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
      
      toast.success(vendorName ? `Opened ${vendorName} order` : "Order PDF opened");
    } catch (error) {
      toast.error("Failed to open PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  // Share PDF using native share API
  const handleSharePdf = async (vendorName = null) => {
    setIsDownloading(true);
    try {
      const params = vendorName ? `?vendor_name=${encodeURIComponent(vendorName)}` : "";
      const response = await api.get(`/orders/${orderId}/pdf${params}`, { responseType: "blob" });
      
      const date = order?.created_at?.substring(0, 10) || "order";
      let filename;
      if (vendorName) {
        const safeVendor = vendorName.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
        filename = `The_Virsa_Order_${safeVendor}_${date}.pdf`;
      } else {
        filename = `The_Virsa_Order_${date}.pdf`;
      }
      
      const file = new File([response.data], filename, { type: 'application/pdf' });
      
      // Check if Web Share API is supported
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `The Virsa Order - ${date}`,
          text: vendorName ? `Order for ${vendorName}` : 'Purchase Order'
        });
        toast.success("Shared successfully");
      } else {
        // Fallback: download the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.info("Sharing not supported - downloaded instead");
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error("Failed to share PDF");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRepeatOrder = () => {
    // Store items in session storage for the create order page
    sessionStorage.setItem("repeatOrder", JSON.stringify(editedItems));
    navigate("/create-order?repeat=true");
  };

  const handleAddItems = () => {
    // Navigate to create order page in edit mode for this order
    // Replace history so when user comes back, they don't go to edit page again
    navigate(`/create-order?edit=${orderId}`, { replace: true });
  };

  const handleDeleteOrder = async () => {
    setIsDeleting(true);
    try {
      await deleteOrder(orderId);
      toast.success("Order deleted successfully");
      navigate("/order-history");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete order");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Group items by vendor for display
  const groupedItems = editedItems.reduce((acc, item) => {
    const vendorName = item.vendor_name || "Unknown Vendor";
    if (!acc[vendorName]) {
      acc[vendorName] = [];
    }
    acc[vendorName].push(item);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isPending = order?.status === "pending_review";
  const isFinalized = order?.status === "finalized";
  const isPartialReceived = order?.status === "partially_received";
  const isReceived = order?.status === "received";
  const canEdit = isAdmin && isPending;
  const canReceive = isFinalized || isPartialReceived;
  const canAddItems = isAdmin && (isPending || isFinalized);

  if (isLoading) {
    return (
      <AppLayout title="Order Details" showBack>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Order Details" showBack>
      <div className="p-4 md:p-6 pb-32 max-w-2xl mx-auto">
        {/* Order Header */}
        <Card className="mb-4" data-testid="order-header">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <Badge 
                variant="secondary"
                className={
                  isPending ? "bg-amber-100 text-amber-700" : 
                  isFinalized ? "bg-blue-100 text-blue-700" :
                  isPartialReceived ? "bg-orange-100 text-orange-700" :
                  "bg-kale-100 text-kale-700"
                }
              >
                {isPending ? (
                  <>
                    <Clock className="w-3 h-3 mr-1" />
                    Pending Review
                  </>
                ) : isFinalized ? (
                  <>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Finalized
                  </>
                ) : isPartialReceived ? (
                  <>
                    <Clock className="w-3 h-3 mr-1" />
                    Partial Received
                  </>
                ) : (
                  <>
                    <PackageCheck className="w-3 h-3 mr-1" />
                    Received
                  </>
                )}
              </Badge>
              <span className="text-xs text-stone-400 font-mono">
                #{order.order_name || orderId.substring(0, 8)}
              </span>
            </div>
            <p className="text-sm text-stone-600">
              Created by <span className="font-medium">{order.created_by_name}</span>
            </p>
            <p className="text-xs text-stone-400 mt-1">
              {formatDate(order.created_at)}
            </p>
            {order.finalized_at && (
              <p className="text-xs text-blue-600 mt-1">
                Finalized: {formatDate(order.finalized_at)}
              </p>
            )}
            {order.received_at && (
              <p className="text-xs text-kale-600 mt-1">
                Fully Received: {formatDate(order.received_at)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Vendor Receive Status - Show for finalized/partial orders */}
        {canReceive && vendorStatus && (
          <Card className="mb-4 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-medium text-stone-700 mb-3 flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Receive by Vendor
              </h3>
              <div className="space-y-2">
                {vendorStatus.vendors.map((vendor) => (
                  <div key={vendor.vendor_name}>
                    {vendor.is_received ? (
                      <div className="flex items-center justify-between p-3 bg-kale-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-kale-600" />
                          <div>
                            <p className="font-medium text-stone-700">{vendor.vendor_name}</p>
                            <p className="text-xs text-stone-500">
                              {vendor.item_count} items • Received
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={`/orders/${orderId}/receive/${encodeURIComponent(vendor.vendor_name)}`}
                        className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Truck className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-stone-900">{vendor.vendor_name}</p>
                            <p className="text-xs text-stone-500">
                              {vendor.item_count} items • Tap to receive
                            </p>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-blue-400" />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Items by Vendor */}

        {/* Items grouped by vendor */}
        {Object.entries(groupedItems).map(([vendorName, items]) => (
          <Card key={vendorName} className="mb-4 animate-in" data-testid={`vendor-group-${vendorName}`}>
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="w-4 h-4 text-kale-600" />
                {vendorName}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-4">
                {items.map((item, idx) => {
                  const globalIndex = editedItems.findIndex(
                    (i) => i.item_id === item.item_id
                  );
                  return (
                    <div 
                      key={item.item_id} 
                      className="border-b border-stone-100 pb-4 last:border-0 last:pb-0"
                      data-testid={`order-item-${item.item_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-stone-900">{item.item_name}</h4>
                          <span className="text-xs font-mono text-stone-500">
                            {item.unit}{item.pack_size ? ` • ${item.pack_size}` : ''}
                          </span>
                        </div>
                        {!canEdit && (
                          <div className="text-right">
                            <span className="font-mono text-lg font-semibold text-kale-700">
                              {item.quantity_to_order}
                            </span>
                            <p className="text-xs text-stone-400">ordered</p>
                            {isReceived && item.quantity_received !== undefined && (
                              <>
                                <span className={`font-mono text-lg font-semibold ${item.quantity_received === item.quantity_to_order ? 'text-kale-700' : 'text-amber-600'}`}>
                                  {item.quantity_received}
                                </span>
                                <p className="text-xs text-stone-400">received</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Show In Hand for Admin (read-only info) */}
                      {isAdmin && (
                        <div className="flex items-center gap-4 mb-2 py-2 px-3 bg-stone-50 rounded-lg flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-stone-500">In Hand:</span>
                            <span className="font-mono text-sm font-medium text-stone-700">
                              {item.quantity_in_hand || 0} {item.unit}
                            </span>
                          </div>
                          {!canEdit && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-stone-500">Ordered:</span>
                              <span className="font-mono text-sm font-medium text-kale-700">
                                {item.quantity_to_order || 0} {item.unit}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {canEdit && (
                        <>
                          {/* Vendor Selector */}
                          <Select
                            value={item.vendor_id}
                            onValueChange={(v) => updateItem(globalIndex, "vendor_id", v)}
                          >
                            <SelectTrigger className="h-10 mb-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {vendors.map((vendor) => (
                                <SelectItem key={vendor.id} value={vendor.id}>
                                  {vendor.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Quantity */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-stone-500 w-20">To Order:</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10"
                              onClick={() => adjustQuantity(globalIndex, -1)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Input
                              type="number"
                              min="0"
                              value={item.quantity_to_order}
                              onChange={(e) =>
                                updateItem(globalIndex, "quantity_to_order", Math.max(0, parseFloat(e.target.value) || 0))
                              }
                              className="h-10 w-20 text-center font-mono"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10"
                              onClick={() => adjustQuantity(globalIndex, 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Comments */}
                          <Textarea
                            placeholder="Comments"
                            value={item.comments || ""}
                            onChange={(e) => updateItem(globalIndex, "comments", e.target.value)}
                            className="min-h-[40px] resize-none text-sm"
                            rows={1}
                          />
                        </>
                      )}

                      {!canEdit && item.comments && (
                        <p className="text-sm text-stone-500 mt-1 italic">
                          "{item.comments}"
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sticky Actions */}
      <div className="sticky-bottom space-y-2">
        {canEdit && (
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              variant="outline"
              className="flex-1 h-12"
              disabled={isSaving}
              data-testid="save-order-button"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
            <Button
              onClick={handleFinalize}
              className="flex-1 btn-accent"
              disabled={isFinalizing}
              data-testid="finalize-order-button"
            >
              {isFinalizing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Finalize
                </>
              )}
            </Button>
          </div>
        )}

        <div className="flex gap-2">
          {/* Open PDF Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={canEdit ? "outline" : "default"}
                className={`flex-1 h-12 ${!canEdit ? 'btn-primary' : ''}`}
                disabled={isDownloading}
                data-testid="open-pdf-button"
              >
                {isDownloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View PDF
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-64 mb-2">
              <DropdownMenuItem 
                onClick={() => handleOpenPdf(null)}
                className="cursor-pointer py-3"
                data-testid="open-full-order"
              >
                <FileText className="w-4 h-4 mr-2" />
                Full Order (All Vendors)
              </DropdownMenuItem>
              
              {orderVendors.length > 1 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    View by Vendor
                  </div>
                  {orderVendors.map((vendorName) => (
                    <DropdownMenuItem 
                      key={vendorName}
                      onClick={() => handleOpenPdf(vendorName)}
                      className="cursor-pointer py-3"
                      data-testid={`open-vendor-${vendorName}`}
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      {vendorName}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Share PDF Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 h-12"
                disabled={isDownloading}
                data-testid="share-pdf-button"
              >
                {isDownloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-64 mb-2">
              <DropdownMenuItem 
                onClick={() => handleSharePdf(null)}
                className="cursor-pointer py-3"
                data-testid="share-full-order"
              >
                <FileText className="w-4 h-4 mr-2" />
                Full Order (All Vendors)
              </DropdownMenuItem>
              
              {orderVendors.length > 1 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    Share by Vendor
                  </div>
                  {orderVendors.map((vendorName) => (
                    <DropdownMenuItem 
                      key={vendorName}
                      onClick={() => handleSharePdf(vendorName)}
                      className="cursor-pointer py-3"
                      data-testid={`share-vendor-${vendorName}`}
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      {vendorName}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleRepeatOrder}
            variant="outline"
            className="flex-1 h-12"
            data-testid="repeat-order-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Repeat Order
          </Button>
          
          {/* Add Items Button - for pending_review and finalized orders */}
          {canAddItems && (
            <Button
              onClick={handleAddItems}
              className="flex-1 h-12 btn-primary"
              data-testid="add-items-button"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Items
            </Button>
          )}
        </div>

        {/* Delete Button - Admin Only */}
        {isAdmin && (
          <Button
            onClick={() => setShowDeleteDialog(true)}
            variant="destructive"
            className="w-full h-12"
            disabled={isDeleting}
            data-testid="delete-order-button"
          >
            {isDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Order
              </>
            )}
          </Button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Order?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this order? This action cannot be undone.
              <br />
              <span className="font-medium mt-2 block">
                Order #{orderId.substring(0, 8)} • {order?.items?.length || 0} items
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOrder}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
